import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';
import React from 'react';

import { logger } from '@/lib/logger';

import type { ProposalDetail } from '../../lib/proposals/core/types';
import { proposalQueryKeys } from '../../lib/proposals/query/keys';

interface ProposalRefreshContextValue {
  /**
   * Trigger a refresh of the proposal data.
   * Returns the fresh data from the cache after refetch.
   * Callers should use the returned data to update local state if needed.
   */
  triggerRefresh: () => Promise<ProposalDetail | undefined>;
  /**
   * Subscribe to refresh events. Callback receives fresh proposal data
   * directly to avoid stale closure issues.
   */
  onRefresh: (
    callback: (data: ProposalDetail | undefined) => void
  ) => () => void;
}

const ProposalRefreshContext =
  createContext<ProposalRefreshContextValue | null>(null);

interface ProposalRefreshProviderProps {
  orgId: number;
  proposalId: number;
  children: ReactNode;
}

/**
 * Provider that manages proposal refresh events.
 *
 * When triggerRefresh() is called:
 * 1. Invalidates React Query cache (bypasses staleTime)
 * 2. Refetches proposal data
 * 3. Notifies all subscribers
 *
 * @example
 * ```tsx
 * <ProposalRefreshProvider orgId={1} proposalId={42}>
 *   <ProposalDetailPanel />
 * </ProposalRefreshProvider>
 * ```
 */
export function ProposalRefreshProvider({
  orgId,
  proposalId,
  children,
}: ProposalRefreshProviderProps) {
  const queryClient = useQueryClient();
  // Subscribers receive fresh data directly to avoid stale closure issues
  const subscribersRef = useRef<
    Set<(data: ProposalDetail | undefined) => void>
  >(new Set());

  const triggerRefresh = useCallback(async (): Promise<
    ProposalDetail | undefined
  > => {
    const queryKey = proposalQueryKeys.detail(orgId, proposalId);
    logger.debug('[useProposalRefresh] triggerRefresh called', {
      orgId,
      proposalId,
      queryKey,
    });
    try {
      // Invalidate to mark as stale (bypasses staleTime)
      logger.debug('[useProposalRefresh] Invalidating queries...');
      await queryClient.invalidateQueries({
        queryKey,
      });
      logger.debug('[useProposalRefresh] Invalidation complete, refetching...');
      // Refetch to get fresh data
      const result = await queryClient.refetchQueries({
        queryKey,
      });
      logger.debug('[useProposalRefresh] Refetch complete', { result });

      // Get fresh data from cache to pass to subscribers
      // This ensures subscribers get the ACTUAL fresh data, not stale closure values
      const freshData = queryClient.getQueryData<ProposalDetail>(queryKey);
      logger.debug('[useProposalRefresh] Fresh data from cache', {
        proposalId: freshData?.id,
        actionsCount: freshData?.actions?.length,
        discussionConversation: freshData?.discussion_conversation,
        primaryActionId: freshData?.actions?.[0]?.id,
      });

      // Notify all subscribers with fresh data (fire-and-forget, with error isolation)
      logger.debug('[useProposalRefresh] Notifying subscribers', {
        count: subscribersRef.current.size,
      });
      subscribersRef.current.forEach(callback => {
        try {
          callback(freshData);
        } catch (e) {
          logger.error(
            'Subscriber error',
            e instanceof Error ? e : new Error(String(e)),
            { component: 'proposals', module: 'useProposalRefresh' }
          );
        }
      });

      // Return fresh data so callers can also use it directly
      return freshData;
    } catch (error) {
      logger.error(
        'Refresh failed',
        error instanceof Error ? error : new Error(String(error)),
        { component: 'proposals', module: 'useProposalRefresh' }
      );
      // Don't throw - user can manually refresh if needed
      return undefined;
    }
  }, [queryClient, orgId, proposalId]);

  const onRefresh = useCallback(
    (callback: (data: ProposalDetail | undefined) => void) => {
      subscribersRef.current.add(callback);
      return () => {
        subscribersRef.current.delete(callback);
      };
    },
    []
  );

  // Both callbacks are already stable, but an inline object literal here would
  // be a NEW context value on every render of this provider — re-rendering
  // every consumer and re-running their `onRefresh` subscribe effects. The
  // provider sits above the whole proposal detail pane, so that fires on any
  // parent re-render (import-status polls, chat progress, cache writes).
  const value = useMemo(
    () => ({ triggerRefresh, onRefresh }),
    [triggerRefresh, onRefresh]
  );

  return React.createElement(
    ProposalRefreshContext.Provider,
    { value },
    children
  );
}

/**
 * Hook to access proposal refresh functionality.
 *
 * Must be used within a ProposalRefreshProvider.
 *
 * @returns Object with triggerRefresh() and onRefresh() functions
 *
 * @example
 * ```tsx
 * function ProposalComponent() {
 *   const { triggerRefresh } = useProposalRefresh();
 *
 *   const handleAIComplete = async () => {
 *     await triggerRefresh(); // Invalidates cache and refetches
 *   };
 * }
 * ```
 *
 * @example
 * ```tsx
 * function VersionEntity() {
 *   const { onRefresh } = useProposalRefresh();
 *
 *   useEffect(() => {
 *     return onRefresh((freshProposal) => {
 *       // Called with fresh data when refresh completes
 *       // Use fresh data directly - no stale closures!
 *       if (freshProposal) {
 *         const action = getPrimaryAction(freshProposal);
 *         fetchVersionsForAction(action?.id);
 *       }
 *     });
 *   }, [onRefresh]);
 * }
 * ```
 */
export function useProposalRefresh() {
  const context = useContext(ProposalRefreshContext);
  if (!context) {
    throw new Error(
      'useProposalRefresh must be used within ProposalRefreshProvider'
    );
  }
  return context;
}
